import { supabase } from './supabase';

/**
 * Generate a cryptographically random share token.
 */
function generateToken() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Create a share link for a record.
 *
 * The caller is responsible for:
 *   1. Deriving the KEK and unwrapping the file key from record.metadata
 *   2. Generating a random share key
 *   3. Re-wrapping the file key with the share key → wrappedKeyForShare
 *   4. Placing the share key in the URL fragment (#shareKeyB64)
 *
 * This function stores only the share-key-wrapped file key.
 * The share key itself never reaches this function or any server.
 *
 * @param {object} record           - Full record object from Supabase
 * @param {string} userId           - Privy user ID (owner)
 * @param {number} expiryHours      - How long the link stays valid
 * @param {string} wrappedKeyForShare - Base64 file key wrapped with the share key
 * @returns {Promise<{ data: object|null, error: object|null }>}
 */
export async function createShareLink(record, userId, expiryHours = 24, wrappedKeyForShare) {
  const token = generateToken();
  const expiresAt = new Date(Date.now() + expiryHours * 60 * 60 * 1000).toISOString();

  const { iv, originalFileName, originalFileType, recordName, recordType } = record.metadata || {};

  const { data, error } = await supabase
    .from('share_links')
    .insert({
      record_id: record.id,
      user_id: userId,
      token,
      expires_at: expiresAt,
      revoked: false,
      cid: record.cid,
      encrypted_key: wrappedKeyForShare,   // share-key-wrapped — not the plain file key
      iv,
      file_name: recordName || originalFileName || 'Medical Record',
      file_type: originalFileType || 'application/octet-stream',
      record_type: recordType || null,
    })
    .select()
    .single();

  return { data, error };
}

/**
 * Fetch a share link by token and validate it is still active.
 *
 * @param {string} token
 * @returns {Promise<{ link: object|null, error: string|null }>}
 */
export async function getShareLink(token) {
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('token', token)
    .single();

  if (error || !data) {
    return { link: null, error: 'Link not found.' };
  }

  if (data.revoked) {
    return { link: null, error: 'This link has been revoked by the owner.' };
  }

  if (new Date(data.expires_at) < new Date()) {
    return { link: null, error: 'This link has expired.' };
  }

  return { link: data, error: null };
}

/**
 * Revoke a share link by its row ID.
 *
 * @param {string} id - UUID of the share_links row
 * @returns {Promise<{ error: object|null }>}
 */
export async function revokeShareLink(id) {
  const { error } = await supabase
    .from('share_links')
    .update({ revoked: true })
    .eq('id', id);

  return { error };
}

/**
 * Record a view on a share link — increments view_count and sets last_viewed_at.
 * Called from the public ShareView page when a valid link is opened.
 *
 * @param {string} id - UUID of the share_links row
 * @param {number} currentCount - current view_count value from the fetched row
 */
export async function recordShareLinkView(id, currentCount = 0) {
  await supabase
    .from('share_links')
    .update({
      view_count: currentCount + 1,
      last_viewed_at: new Date().toISOString(),
    })
    .eq('id', id);
}

/**
 * List all active (non-revoked, non-expired) share links for a record.
 *
 * @param {string} recordId - UUID of the record
 * @param {string} userId   - Privy user ID (safety check)
 * @returns {Promise<{ data: object[], error: object|null }>}
 */
export async function getShareLinksForRecord(recordId, userId) {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('record_id', recordId)
    .eq('user_id', userId)
    .eq('revoked', false)
    .gt('expires_at', now)
    .order('created_at', { ascending: false });

  return { data: data || [], error };
}

/**
 * List all active (non-revoked, non-expired) share links for a user across all records.
 *
 * @param {string} userId - Privy user ID
 * @returns {Promise<{ data: object[], error: object|null }>}
 */
export async function getAllShareLinksForUser(userId) {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('user_id', userId)
    .eq('revoked', false)
    .gt('expires_at', now)
    .order('created_at', { ascending: false });

  return { data: data || [], error };
}
